import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import './App.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginForm from './components/auth/LoginForm';
import AdminLayout, { AdminContextOutlet } from './components/admin/AdminLayout';
import AdminDashboardPage from './components/admin/pages/AdminDashboardPage';
import AdminClinicalAnalyticsLayout, {
  AdminNursingAnalyticsLayout
} from './components/admin/reports/AdminClinicalAnalyticsLayout';
import AdminReceptionReportsLayout from './components/admin/reports/AdminReceptionReportsLayout';
import AdminReceptionPassagesPage from './components/admin/reports/AdminReceptionPassagesPage';
import AdminReceptionRdvPage from './components/admin/reports/AdminReceptionRdvPage';
import AdminPharmacyReportsPage from './components/admin/reports/AdminPharmacyReportsPage';
import AdminSettingsPage from './components/admin/pages/AdminSettingsPage';
import AdminSitesPage from './components/admin/pages/AdminSitesPage';
import AdminPersonnelLayout from './components/admin-personnel/AdminPersonnelLayout';
import AdminPersonnelDashboardPage from './components/admin-personnel/pages/AdminPersonnelDashboardPage';
import AdminPersonnelCompaniesPage from './components/admin-personnel/pages/AdminPersonnelCompaniesPage';
import AdminPersonnelMembersPage from './components/admin-personnel/pages/AdminPersonnelMembersPage';
import AdminPersonnelPricingPage from './components/admin-personnel/pages/AdminPersonnelPricingPage';
import AdminPersonnelInvoicesPage from './components/admin-personnel/pages/AdminPersonnelInvoicesPage';
import AdminPersonnelNotificationsPage from './components/admin-personnel/pages/AdminPersonnelNotificationsPage';
import AdminPersonnelActivitiesPage from './components/admin-personnel/pages/AdminPersonnelActivitiesPage';
import AdminPersonnelPlaceholderPage from './components/admin-personnel/pages/AdminPersonnelPlaceholderPage';
import { AP_COPY } from './components/admin-personnel/adminPersonnelCopy';
import UserManagement from './components/admin/UserManagement';
import CompanyManagement from './components/admin/CompanyManagement';
import AdminMembersPage from './components/admin/pages/AdminMembersPage';
import DoctorLayout from './components/doctor/DoctorLayout';
import DoctorDashboardPage from './components/doctor/pages/DoctorDashboardPage';
import DoctorConsultationsPage from './components/doctor/pages/DoctorConsultationsPage';
import DoctorConsultationEditorPage from './components/doctor/pages/DoctorConsultationEditorPage';
import DoctorPrescriptionsPage from './components/doctor/pages/DoctorPrescriptionsPage';
import DoctorModulePlaceholderPage from './components/doctor/pages/DoctorModulePlaceholderPage';
import DoctorDossierPage from './components/doctor/pages/DoctorDossierPage';
import DoctorAppointmentsPage from './components/doctor/pages/DoctorAppointmentsPage';
import DoctorRestDaysPage from './components/doctor/pages/DoctorRestDaysPage';
import HeadDoctorLayout from './components/head-doctor/HeadDoctorLayout';
import HeadDoctorDashboardPage from './components/head-doctor/pages/HeadDoctorDashboardPage';
import HeadDoctorConsultationsPage from './components/head-doctor/pages/HeadDoctorConsultationsPage';
import HeadDoctorConsultationEditorPage from './components/head-doctor/pages/HeadDoctorConsultationEditorPage';
import HeadDoctorDossierPage from './components/head-doctor/pages/HeadDoctorDossierPage';
import HeadDoctorDiseasesPage from './components/head-doctor/pages/HeadDoctorDiseasesPage';
import HeadDoctorAnalysesPage from './components/head-doctor/pages/HeadDoctorAnalysesPage';
import HeadDoctorRadiographiesPage from './components/head-doctor/pages/HeadDoctorRadiographiesPage';
import HeadDoctorEchographiesPage from './components/head-doctor/pages/HeadDoctorEchographiesPage';
import HeadDoctorCarePartnersPage from './components/head-doctor/pages/HeadDoctorCarePartnersPage';
import HeadDoctorCareReferralsPage from './components/head-doctor/pages/HeadDoctorCareReferralsPage';
import HeadDoctorRestDaysPage from './components/head-doctor/pages/HeadDoctorRestDaysPage';
import HeadDoctorNursingCarePage from './components/head-doctor/pages/HeadDoctorNursingCarePage';
import HeadDoctorNursingTestsPage from './components/head-doctor/pages/HeadDoctorNursingTestsPage';
import HeadDoctorVaccinesPage from './components/head-doctor/pages/HeadDoctorVaccinesPage';
import HeadDoctorFamilyPlanningPage from './components/head-doctor/pages/HeadDoctorFamilyPlanningPage';
import HeadDoctorAppointmentsPage from './components/head-doctor/pages/HeadDoctorAppointmentsPage';
import HeadDoctorReportsLayout from './components/head-doctor/reports/HeadDoctorReportsLayout';
import HeadDoctorReportsSynthesePage from './components/head-doctor/reports/HeadDoctorReportsSynthesePage';
import HeadDoctorReportsPathologiesPage from './components/head-doctor/reports/HeadDoctorReportsPathologiesPage';
import HeadDoctorReportsMorbiditePage from './components/head-doctor/reports/HeadDoctorReportsMorbiditePage';
import HeadDoctorReportsOrdonnancesPage from './components/head-doctor/reports/HeadDoctorReportsOrdonnancesPage';
import HeadDoctorReportsMedicamentsPage from './components/head-doctor/reports/HeadDoctorReportsMedicamentsPage';
import HeadDoctorReportsConsultationsPage from './components/head-doctor/reports/HeadDoctorReportsConsultationsPage';
import HeadDoctorReportsSoinsPage from './components/head-doctor/reports/HeadDoctorReportsSoinsPage';
import HeadDoctorReportsExamensPage from './components/head-doctor/reports/HeadDoctorReportsExamensPage';
import HeadDoctorReportsReposPage from './components/head-doctor/reports/HeadDoctorReportsReposPage';
import HeadDoctorReportsWorkAccidentsPage from './components/head-doctor/reports/HeadDoctorReportsWorkAccidentsPage';
import HeadDoctorReportsOccupationalDiseasesPage from './components/head-doctor/reports/HeadDoctorReportsOccupationalDiseasesPage';
import DentistLayout from './components/dentist/DentistLayout';
import DentistDashboardPage from './components/dentist/pages/DentistDashboardPage';
import DentistConsultationsPage from './components/dentist/pages/DentistConsultationsPage';
import DentistConsultationEditorPage from './components/dentist/pages/DentistConsultationEditorPage';
import DentistDossierPage from './components/dentist/pages/DentistDossierPage';
import DentistAppointmentsPage from './components/dentist/pages/DentistAppointmentsPage';
import DentistModulePlaceholderPage from './components/dentist/pages/DentistModulePlaceholderPage';
import NurseLayout from './components/nurse/NurseLayout';
import NurseDashboardPage from './components/nurse/pages/NurseDashboardPage';
import NurseQueuePage from './components/nurse/pages/NurseQueuePage';
import NurseConsultationEditorPage from './components/nurse/pages/NurseConsultationEditorPage';
import NurseAccueilPage from './components/nurse/pages/NurseAccueilPage';
import NurseVisitsHistoryPage from './components/nurse/pages/NurseVisitsHistoryPage';
import NurseGuidePage from './components/nurse/pages/NurseGuidePage';
import MajorLayout from './components/major/MajorLayout';
import MajorNursingCarePage from './components/major/pages/MajorNursingCarePage';
import MajorInjectionPage from './components/major/pages/MajorInjectionPage';
import MajorNursingTestsPage from './components/major/pages/MajorNursingTestsPage';
import MajorVaccinesPage from './components/major/pages/MajorVaccinesPage';
import MajorFamilyPlanningPage from './components/major/pages/MajorFamilyPlanningPage';
import MajorReportsLayout from './components/major/reports/MajorReportsLayout';
import MajorReportsOverviewPage from './components/major/reports/MajorReportsOverviewPage';
import MajorReportsSoinsPage from './components/major/reports/MajorReportsSoinsPage';
import MajorReportsInjectionsPage from './components/major/reports/MajorReportsInjectionsPage';
import MajorReportsTestsPage from './components/major/reports/MajorReportsTestsPage';
import MajorReportsVaccinsPage from './components/major/reports/MajorReportsVaccinsPage';
import MajorReportsPlanningFamilialPage from './components/major/reports/MajorReportsPlanningFamilialPage';
import LocalPharmacyPage from './components/clinical-pharmacy/LocalPharmacyPage';
import CentralPharmacyLayout from './components/central-pharmacy/CentralPharmacyLayout';
import PharmacistLayout from './components/central-pharmacy/PharmacistLayout';
import PharmacyDashboardPage from './components/central-pharmacy/pages/PharmacyDashboardPage';
import PharmacistDashboardPage from './components/central-pharmacy/pages/PharmacistDashboardPage';
import PharmacyCatalogPage from './components/central-pharmacy/pages/PharmacyCatalogPage';
import PharmacyCentralStockPage from './components/central-pharmacy/pages/PharmacyCentralStockPage';
import PharmacyDutyCabinetPage from './components/central-pharmacy/pages/PharmacyDutyCabinetPage';
import PharmacySiteStocksPage from './components/central-pharmacy/pages/PharmacySiteStocksPage';
import PharmacySuppliersPage from './components/central-pharmacy/pages/PharmacySuppliersPage';
import PharmacyOrdersPage from './components/central-pharmacy/pages/PharmacyOrdersPage';
import PharmacyOrderCreatePage from './components/central-pharmacy/pages/PharmacyOrderCreatePage';
import PharmacyOrderDetailPage from './components/central-pharmacy/pages/PharmacyOrderDetailPage';
import PharmacyRequestsPage from './components/central-pharmacy/pages/PharmacyRequestsPage';
import PharmacyDistributionsPage from './components/central-pharmacy/pages/PharmacyDistributionsPage';
import PharmacyDistributionCreatePage from './components/central-pharmacy/pages/PharmacyDistributionCreatePage';
import PharmacyDistributionDetailPage from './components/central-pharmacy/pages/PharmacyDistributionDetailPage';
import PharmacyDispensationsPage from './components/central-pharmacy/pages/PharmacyDispensationsPage';
import PharmacyDispensationCreatePage from './components/central-pharmacy/pages/PharmacyDispensationCreatePage';
import PharmacyDispensationDetailPage from './components/central-pharmacy/pages/PharmacyDispensationDetailPage';
import PharmacyInteractionsPage from './components/central-pharmacy/pages/PharmacyInteractionsPage';
import PharmacyAlertsPage from './components/central-pharmacy/pages/PharmacyAlertsPage';
import PharmacyMovementsPage from './components/central-pharmacy/pages/PharmacyMovementsPage';
import PharmacyInventoriesPage from './components/central-pharmacy/pages/PharmacyInventoriesPage';
import PharmacyInventoryDetailPage from './components/central-pharmacy/pages/PharmacyInventoryDetailPage';
import PharmacyReportsPage from './components/central-pharmacy/pages/PharmacyReportsPage';
import UserDashboard from './components/user/UserDashboard';
import ReceptionistLayout from './components/receptionist/ReceptionistLayout';
import ReceptionDashboardPage from './components/receptionist/pages/ReceptionDashboardPage';
import ReceptionAppointmentsPage from './components/receptionist/pages/ReceptionAppointmentsPage';
import ReceptionReportingPage from './components/receptionist/pages/ReceptionReportingPage';
import ReceptionCompaniesPage from './components/receptionist/pages/ReceptionCompaniesPage';
import ReceptionMembersPage from './components/receptionist/pages/ReceptionMembersPage';
import ReceptionExternalMembersPage from './components/receptionist/pages/ReceptionExternalMembersPage';
import UnauthorizedPage from './components/common/UnauthorizedPage';
import RoleGuard, { AdminGuard, UserManagementGuard, CompanyManagementGuard, ReportsGuard, SettingsGuard } from './components/common/RoleGuard';
import { ROLES } from './constants/roles';
import { AppLoaderGate } from './components/common/loader';


// Composant pour les routes protégées
const AppRoutes = () => {
  const { user, loading, getRedirectUrl } = useAuth();

  return (
    <AppLoaderGate
      isLoading={loading}
      loaderProps={{ statusMessage: 'Initialisation de la session...' }}
    >
    <Routes>
      {/* Route de connexion */}
      <Route 
        path="/login" 
        element={user ? <Navigate to={getRedirectUrl(user.role)} replace /> : <LoginForm />} 
      />
      
      {/* Route d'erreur 403 */}
      <Route 
        path="/unauthorized" 
        element={<UnauthorizedPage />} 
      />
      
      {/* Routes protégées par rôles */}
      <Route
        path="/admin"
        element={
          <AdminGuard fallbackPath="/unauthorized">
            <AdminLayout />
          </AdminGuard>
        }
      >
        <Route index element={<AdminDashboardPage />} />
        <Route
          path="users"
          element={
            <UserManagementGuard fallbackPath="/unauthorized">
              <UserManagement />
            </UserManagementGuard>
          }
        />
        <Route
          path="company"
          element={
            <CompanyManagementGuard fallbackPath="/unauthorized">
              <CompanyManagement />
            </CompanyManagementGuard>
          }
        />
        <Route
          path="members"
          element={
            <CompanyManagementGuard fallbackPath="/unauthorized">
              <AdminMembersPage />
            </CompanyManagementGuard>
          }
        />
        <Route
          path="sites"
          element={
            <CompanyManagementGuard fallbackPath="/unauthorized">
              <AdminSitesPage />
            </CompanyManagementGuard>
          }
        />
        <Route
          path="reports"
          element={
            <ReportsGuard fallbackPath="/unauthorized">
              <AdminContextOutlet />
            </ReportsGuard>
          }
        >
          <Route index element={<Navigate to="clinique/synthese" replace />} />

          <Route path="reception" element={<AdminReceptionReportsLayout />}>
            <Route index element={<Navigate to="passages" replace />} />
            <Route path="passages" element={<AdminReceptionPassagesPage />} />
            <Route path="rendez-vous" element={<AdminReceptionRdvPage />} />
          </Route>

          <Route path="clinique" element={<AdminClinicalAnalyticsLayout />}>
            <Route index element={<Navigate to="synthese" replace />} />
            <Route path="synthese" element={<HeadDoctorReportsSynthesePage />} />
            <Route path="consultations" element={<HeadDoctorReportsConsultationsPage />} />
            <Route path="jours-repos" element={<HeadDoctorReportsReposPage />} />
            <Route path="morbidite" element={<HeadDoctorReportsMorbiditePage />} />
            <Route path="pathologies" element={<HeadDoctorReportsPathologiesPage />} />
            <Route path="ordonnances" element={<HeadDoctorReportsOrdonnancesPage />} />
            <Route path="medicaments" element={<HeadDoctorReportsMedicamentsPage />} />
            <Route path="examens" element={<HeadDoctorReportsExamensPage />} />
            <Route path="accidents-travail" element={<HeadDoctorReportsWorkAccidentsPage />} />
            <Route path="maladies-professionnelles" element={<HeadDoctorReportsOccupationalDiseasesPage />} />
          </Route>

          <Route path="infirmerie" element={<AdminNursingAnalyticsLayout />}>
            <Route index element={<MajorReportsOverviewPage />} />
            <Route path="soins" element={<MajorReportsSoinsPage />} />
            <Route path="injections" element={<MajorReportsInjectionsPage />} />
            <Route path="tests" element={<MajorReportsTestsPage />} />
            <Route path="vaccins" element={<MajorReportsVaccinsPage />} />
            <Route path="planning-familial" element={<MajorReportsPlanningFamilialPage />} />
          </Route>

          <Route path="pharmacie" element={<AdminPharmacyReportsPage />} />

          {/* Legacy redirects */}
          <Route path="synthese" element={<Navigate to="/admin/reports/clinique/synthese" replace />} />
          <Route path="consultations" element={<Navigate to="/admin/reports/clinique/consultations" replace />} />
          <Route path="jours-repos" element={<Navigate to="/admin/reports/clinique/jours-repos" replace />} />
          <Route path="morbidite" element={<Navigate to="/admin/reports/clinique/morbidite" replace />} />
          <Route path="pathologies" element={<Navigate to="/admin/reports/clinique/pathologies" replace />} />
          <Route path="ordonnances" element={<Navigate to="/admin/reports/clinique/ordonnances" replace />} />
          <Route path="medicaments" element={<Navigate to="/admin/reports/clinique/medicaments" replace />} />
          <Route path="examens" element={<Navigate to="/admin/reports/clinique/examens" replace />} />
          <Route path="soins-infirmerie" element={<Navigate to="/admin/reports/infirmerie" replace />} />
          <Route path="accidents-travail" element={<Navigate to="/admin/reports/clinique/accidents-travail" replace />} />
          <Route path="maladies-professionnelles" element={<Navigate to="/admin/reports/clinique/maladies-professionnelles" replace />} />
          <Route path="analytics" element={<Navigate to="/admin/reports/clinique/synthese" replace />} />
          <Route path="analytics/*" element={<Navigate to="/admin/reports/clinique/synthese" replace />} />
        </Route>
        <Route
          path="settings"
          element={
            <SettingsGuard fallbackPath="/unauthorized">
              <AdminSettingsPage />
            </SettingsGuard>
          }
        />
      </Route>
      
      <Route
        path="/admin-personnel"
        element={
          <RoleGuard requiredRoles="admin personnel" fallbackPath="/unauthorized">
            <AdminPersonnelLayout />
          </RoleGuard>
        }
      >
        <Route index element={<AdminPersonnelDashboardPage />} />
        <Route path="companies" element={<AdminPersonnelCompaniesPage />} />
        <Route path="users" element={<AdminPersonnelMembersPage />} />
        <Route path="pricing" element={<AdminPersonnelPricingPage />} />
        <Route path="invoices" element={<AdminPersonnelInvoicesPage />} />
        <Route path="profile" element={<AdminPersonnelPlaceholderPage placeholderKey="profile" />} />
        <Route path="settings" element={<AdminPersonnelPlaceholderPage placeholderKey="settings" />} />
        <Route path="reports" element={<AdminPersonnelPlaceholderPage placeholderKey="reports" />} />
        <Route path="activities" element={<AdminPersonnelActivitiesPage />} />
        <Route path="notifications" element={<AdminPersonnelNotificationsPage />} />
      </Route>

      <Route
        path="/doctor"
        element={
          <RoleGuard requiredRoles={[ROLES.DOCTOR]} fallbackPath="/unauthorized">
            <DoctorLayout />
          </RoleGuard>
        }
      >
        <Route index element={<DoctorDashboardPage />} />
        <Route path="consultations/new" element={<DoctorConsultationEditorPage />} />
        <Route path="consultations/:consultationId/edit" element={<DoctorConsultationEditorPage />} />
        <Route path="consultations" element={<DoctorConsultationsPage />} />
        <Route path="prescriptions" element={<DoctorPrescriptionsPage />} />
        <Route path="pharmacie" element={<LocalPharmacyPage />} />
        <Route path="dossier" element={<DoctorDossierPage />} />
        <Route path="diagnostic" element={<DoctorModulePlaceholderPage placeholderKey="diagnostic" />} />
        <Route path="examens" element={<DoctorModulePlaceholderPage placeholderKey="examens" />} />
        <Route path="certificats" element={<DoctorModulePlaceholderPage placeholderKey="certificats" />} />
        <Route path="maladies" element={<Navigate to="/doctor" replace />} />
        <Route path="jours-repos" element={<DoctorRestDaysPage />} />
        <Route path="lettres-reference" element={<Navigate to="/doctor" replace />} />
        <Route path="rendez-vous" element={<DoctorAppointmentsPage />} />
        <Route path="*" element={<Navigate to="/doctor" replace />} />
      </Route>

      <Route
        path="/dentist"
        element={
          <RoleGuard requiredRoles={[ROLES.DENTIST]} fallbackPath="/unauthorized">
            <DentistLayout />
          </RoleGuard>
        }
      >
        <Route index element={<DentistDashboardPage />} />
        <Route path="consultations/new" element={<DentistConsultationEditorPage />} />
        <Route path="consultations/:consultationId/edit" element={<DentistConsultationEditorPage />} />
        <Route path="consultations" element={<DentistConsultationsPage />} />
        <Route path="dossier" element={<DentistDossierPage />} />
        <Route path="diagnostic" element={<DentistModulePlaceholderPage placeholderKey="diagnostic" />} />
        <Route path="examens" element={<DentistModulePlaceholderPage placeholderKey="examens" />} />
        <Route path="certificats" element={<DentistModulePlaceholderPage placeholderKey="certificats" />} />
        <Route path="rendez-vous" element={<DentistAppointmentsPage />} />
        <Route path="*" element={<Navigate to="/dentist" replace />} />
      </Route>

      <Route
        path="/head-doctor"
        element={
          <RoleGuard requiredRoles={[ROLES.HEAD_DOCTOR]} fallbackPath="/unauthorized">
            <HeadDoctorLayout />
          </RoleGuard>
        }
      >
        <Route index element={<HeadDoctorDashboardPage />} />
        <Route path="consultations/new" element={<HeadDoctorConsultationEditorPage />} />
        <Route path="consultations/:consultationId/edit" element={<HeadDoctorConsultationEditorPage />} />
        <Route path="consultations" element={<HeadDoctorConsultationsPage />} />
        <Route path="dossier" element={<HeadDoctorDossierPage />} />
        <Route path="maladies" element={<HeadDoctorDiseasesPage />} />
        <Route path="analyses" element={<HeadDoctorAnalysesPage />} />
        <Route path="radiographies" element={<HeadDoctorRadiographiesPage />} />
        <Route path="echographies" element={<HeadDoctorEchographiesPage />} />
        <Route path="partenaires" element={<HeadDoctorCarePartnersPage />} />
        <Route path="orientations" element={<HeadDoctorCareReferralsPage />} />
        <Route path="jours-repos" element={<HeadDoctorRestDaysPage />} />
        <Route path="soins-infirmerie" element={<HeadDoctorNursingCarePage />} />
        <Route path="tests-infirmerie" element={<HeadDoctorNursingTestsPage />} />
        <Route path="soins-infirmierie" element={<Navigate to="/head-doctor/soins-infirmerie" replace />} />
        <Route path="tests-infirmierie" element={<Navigate to="/head-doctor/tests-infirmerie" replace />} />
        <Route path="vaccins" element={<HeadDoctorVaccinesPage />} />
        <Route path="planning-familial" element={<HeadDoctorFamilyPlanningPage />} />
        <Route path="rendez-vous" element={<HeadDoctorAppointmentsPage />} />
        <Route path="statistiques" element={<HeadDoctorReportsLayout />}>
          <Route index element={<Navigate to="synthese" replace />} />
          <Route path="synthese" element={<HeadDoctorReportsSynthesePage />} />
          <Route path="consultations" element={<HeadDoctorReportsConsultationsPage />} />
          <Route path="accidents-travail" element={<HeadDoctorReportsWorkAccidentsPage />} />
          <Route
            path="maladies-professionnelles"
            element={<HeadDoctorReportsOccupationalDiseasesPage />}
          />
          <Route path="pathologies" element={<HeadDoctorReportsPathologiesPage />} />
          <Route path="morbidite" element={<HeadDoctorReportsMorbiditePage />} />
          <Route path="ordonnances" element={<HeadDoctorReportsOrdonnancesPage />} />
          <Route path="medicaments" element={<HeadDoctorReportsMedicamentsPage />} />
          <Route path="soins-infirmerie" element={<HeadDoctorReportsSoinsPage />} />
          <Route path="soins-infirmierie" element={<Navigate to="../soins-infirmerie" replace />} />
          <Route path="examens" element={<HeadDoctorReportsExamensPage />} />
          <Route path="jours-repos" element={<HeadDoctorReportsReposPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/head-doctor" replace />} />
      </Route>

      <Route
        path="/nurse"
        element={
          <RoleGuard requiredRoles={[ROLES.NURSE]} fallbackPath="/unauthorized">
            <NurseLayout />
          </RoleGuard>
        }
      >
        <Route index element={<NurseDashboardPage />} />
        <Route path="accueil" element={<NurseAccueilPage />} />
        <Route path="file" element={<NurseQueuePage />} />
        <Route path="parametrage" element={<Navigate to="/nurse/file?filter=pending" replace />} />
        <Route path="consultations/new" element={<NurseConsultationEditorPage />} />
        <Route path="consultations/:consultationId/edit" element={<NurseConsultationEditorPage />} />
        <Route path="consultations" element={<Navigate to="/nurse/file" replace />} />
        <Route path="historique-passages" element={<NurseVisitsHistoryPage />} />
        <Route path="pharmacie" element={<LocalPharmacyPage />} />
        <Route path="aide" element={<NurseGuidePage />} />
        <Route path="*" element={<Navigate to="/nurse" replace />} />
      </Route>

      <Route
        path="/major"
        element={
          <RoleGuard requiredRoles={[ROLES.MAJOR]} fallbackPath="/unauthorized">
            <MajorLayout />
          </RoleGuard>
        }
      >
        <Route index element={<NurseDashboardPage />} />
        <Route path="accueil" element={<NurseAccueilPage />} />
        <Route path="file" element={<NurseQueuePage />} />
        <Route path="parametrage" element={<Navigate to="/major/file?filter=pending" replace />} />
        <Route path="consultations/new" element={<NurseConsultationEditorPage />} />
        <Route path="consultations/:consultationId/edit" element={<NurseConsultationEditorPage />} />
        <Route path="consultations" element={<Navigate to="/major/file" replace />} />
        <Route path="historique-passages" element={<NurseVisitsHistoryPage />} />
        <Route path="pharmacie" element={<LocalPharmacyPage />} />
        <Route path="aide" element={<NurseGuidePage />} />
        <Route path="soins-infirmerie" element={<MajorNursingCarePage />} />
        <Route path="injections" element={<MajorInjectionPage />} />
        <Route path="tests-infirmerie" element={<MajorNursingTestsPage />} />
        <Route path="vaccins" element={<MajorVaccinesPage />} />
        <Route path="planning-familial" element={<MajorFamilyPlanningPage />} />
        <Route path="rapports" element={<MajorReportsLayout />}>
          <Route index element={<MajorReportsOverviewPage />} />
          <Route path="soins" element={<MajorReportsSoinsPage />} />
          <Route path="injections" element={<MajorReportsInjectionsPage />} />
          <Route path="tests" element={<MajorReportsTestsPage />} />
          <Route path="vaccins" element={<MajorReportsVaccinsPage />} />
          <Route path="planning-familial" element={<MajorReportsPlanningFamilialPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/major" replace />} />
      </Route>

      <Route
        path="/receptionist"
        element={
          <RoleGuard
            requiredRoles={[ROLES.RECEPTIONIST, ROLES.ADMIN, ROLES.ADMIN_PERSONNEL]}
            fallbackPath="/unauthorized"
          >
            <ReceptionistLayout />
          </RoleGuard>
        }
      >
        <Route index element={<ReceptionDashboardPage />} />
        <Route path="rendez-vous" element={<ReceptionAppointmentsPage />} />
        <Route path="reporting" element={<ReceptionReportingPage />} />
        <Route path="societes" element={<ReceptionCompaniesPage />} />
        <Route path="adherents" element={<ReceptionMembersPage />} />
        <Route path="membres-externes" element={<ReceptionExternalMembersPage />} />
        <Route path="*" element={<Navigate to="/receptionist" replace />} />
      </Route>

      <Route
        path="/pharmacy-central"
        element={
          <RoleGuard
            requiredRoles={[ROLES.ADMIN_PHARMACIST, ROLES.ADMIN]}
            fallbackPath="/unauthorized"
          >
            <CentralPharmacyLayout />
          </RoleGuard>
        }
      >
        <Route index element={<PharmacyDashboardPage />} />
        <Route path="catalogue" element={<PharmacyCatalogPage />} />
        <Route path="stock-central" element={<PharmacyCentralStockPage />} />
        <Route path="armoire-garde" element={<PharmacyDutyCabinetPage />} />
        <Route path="stocks-sites" element={<PharmacySiteStocksPage />} />
        <Route path="inventaire" element={<PharmacyInventoriesPage />} />
        <Route path="inventaire/:inventoryId" element={<PharmacyInventoryDetailPage />} />
        <Route path="fournisseurs" element={<PharmacySuppliersPage />} />
        <Route path="commandes" element={<PharmacyOrdersPage />} />
        <Route path="commandes/nouvelle" element={<PharmacyOrderCreatePage />} />
        <Route path="commandes/:orderId" element={<PharmacyOrderDetailPage />} />
        <Route path="demandes" element={<PharmacyRequestsPage />} />
        <Route path="distributions" element={<PharmacyDistributionsPage />} />
        <Route path="distributions/nouvelle" element={<PharmacyDistributionCreatePage />} />
        <Route path="distributions/:distributionId" element={<PharmacyDistributionDetailPage />} />
        <Route path="dispensation" element={<PharmacyDispensationsPage />} />
        <Route path="dispensation/nouvelle" element={<PharmacyDispensationCreatePage />} />
        <Route path="dispensation/:dispensationId" element={<PharmacyDispensationDetailPage />} />
        <Route path="interactions" element={<PharmacyInteractionsPage />} />
        <Route path="alertes" element={<PharmacyAlertsPage />} />
        <Route path="mouvements" element={<PharmacyMovementsPage />} />
        <Route path="rapports" element={<PharmacyReportsPage />} />
        <Route path="*" element={<Navigate to="/pharmacy-central" replace />} />
      </Route>

      <Route
        path="/pharmacist"
        element={
          <RoleGuard
            requiredRoles={[ROLES.PHARMACIST]}
            fallbackPath="/unauthorized"
          >
            <PharmacistLayout />
          </RoleGuard>
        }
      >
        <Route index element={<PharmacistDashboardPage />} />
        <Route path="stocks-sites" element={<PharmacySiteStocksPage />} />
        <Route path="inventaire" element={<PharmacyInventoriesPage />} />
        <Route path="inventaire/:inventoryId" element={<PharmacyInventoryDetailPage />} />
        <Route path="demandes" element={<PharmacyRequestsPage />} />
        <Route
          path="dispensation"
          element={
            <PharmacyDispensationsPage siteScoped hideSiteColumn defaultStatusFilter="PENDING" embedded />
          }
        />
        <Route path="dispensation/nouvelle" element={<PharmacyDispensationCreatePage />} />
        <Route path="dispensation/:dispensationId" element={<PharmacyDispensationDetailPage />} />
        <Route path="alertes" element={<PharmacyAlertsPage />} />
        <Route path="mouvements" element={<PharmacyMovementsPage />} />
        <Route path="*" element={<Navigate to="/pharmacist" replace />} />
      </Route>

      <Route
        path="/pharmacy/*"
        element={
          <Navigate
            to={
              user?.role === ROLES.PHARMACIST
                ? '/pharmacist'
                : '/pharmacy-central'
            }
            replace
          />
        }
      />

      <Route 
        path="/user" 
        element={
          <RoleGuard requiredRoles="user" fallbackPath="/unauthorized">
            <UserDashboard />
          </RoleGuard>
        } 
      />
      
      {/* Routes de redirection */}
      <Route 
        path="/" 
        element={<Navigate to={user ? getRedirectUrl(user.role) : "/login"} replace />} 
      />
      <Route 
        path="*" 
        element={<Navigate to={user ? getRedirectUrl(user.role) : "/login"} replace />} 
      />
    </Routes>
    </AppLoaderGate>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <AppRoutes />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
